// 顧客事前登録Hooks - TanStack Query v5統合
// UNIFIED_DEVELOPMENT_GUIDE準拠

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import type { AxiosError } from 'axios';
import { customerPreRegistrationApi } from '../api';
import type {
  CustomerPreRegistrationFilters,
  CustomerPreRegistrationFormInput,
} from '../types';

// ========== Query Keys ==========

export const customerPreRegistrationKeys = {
  all: ['customer-pre-registrations'] as const,
  lists: () => [...customerPreRegistrationKeys.all, 'list'] as const,
  list: (filters?: CustomerPreRegistrationFilters) =>
    [...customerPreRegistrationKeys.lists(), filters] as const,
  details: () => [...customerPreRegistrationKeys.all, 'detail'] as const,
  detail: (id: number) => [...customerPreRegistrationKeys.details(), id] as const,
  stats: () => [...customerPreRegistrationKeys.all, 'stats'] as const,
};

// ========== Query Hooks ==========

// 一覧取得
export const useCustomerPreRegistrations = (
  params?: CustomerPreRegistrationFilters & { page?: number; per_page?: number }
) => {
  return useQuery({
    queryKey: customerPreRegistrationKeys.list(params),
    queryFn: () => customerPreRegistrationApi.getList(params),
  });
};

// 詳細取得
export const useCustomerPreRegistration = (id: number) => {
  return useQuery({
    queryKey: customerPreRegistrationKeys.detail(id),
    queryFn: () => customerPreRegistrationApi.getById(id),
    enabled: !!id,
  });
};

// 統計取得
export const useCustomerPreRegistrationStats = () => {
  return useQuery({
    queryKey: customerPreRegistrationKeys.stats(),
    queryFn: () => customerPreRegistrationApi.getStats(),
  });
};

// ========== Mutation Hooks ==========

// 公開フォーム登録
export const useCreatePublicPreRegistration = () => {
  return useMutation({
    mutationFn: (data: CustomerPreRegistrationFormInput) =>
      customerPreRegistrationApi.storePublic(data),
    onSuccess: () => {
      notifications.show({
        title: '登録完了',
        message: '事前登録を受け付けました。担当者から連絡いたしますので、しばらくお待ちください。',
        color: 'green',
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      notifications.show({
        title: '登録エラー',
        message: error.response?.data?.message || '登録に失敗しました',
        color: 'red',
      });
    },
  });
};

// 確認済みマーク
export const useConfirmPreRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => customerPreRegistrationApi.confirm(id),
    onSuccess: (data) => {
      // 関連クエリを無効化して再取得
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.stats() });

      notifications.show({
        title: '確認完了',
        message: '事前登録を確認済みにしました',
        color: 'green',
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      notifications.show({
        title: '確認エラー',
        message: error.response?.data?.message || '確認に失敗しました',
        color: 'red',
      });
    },
  });
};

// 正式顧客へ変換
export const useConvertPreRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => customerPreRegistrationApi.convert(id),
    onSuccess: (data) => {
      // 事前登録関連クエリ無効化
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.stats() });

      // 顧客一覧も更新（新規顧客が追加されるため）
      queryClient.invalidateQueries({ queryKey: ['customers'] });

      notifications.show({
        title: '変換完了',
        message: '正式な顧客として登録しました',
        color: 'green',
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      notifications.show({
        title: '変換エラー',
        message: error.response?.data?.message || '変換に失敗しました',
        color: 'red',
      });
    },
  });
};

// ステータス更新
export const useUpdatePreRegistrationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }: { id: number; status: string; notes?: string }) =>
      customerPreRegistrationApi.updateStatus(id, { status, notes }),
    onSuccess: (data) => {
      // 関連クエリを無効化して再取得
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.stats() });

      notifications.show({
        title: 'ステータス更新完了',
        message: 'ステータスを変更しました',
        color: 'green',
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      notifications.show({
        title: 'ステータス更新エラー',
        message: error.response?.data?.message || 'ステータス更新に失敗しました',
        color: 'red',
      });
    },
  });
};

// 削除
export const useDeletePreRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => customerPreRegistrationApi.delete(id),
    onSuccess: () => {
      // 一覧と統計を再取得
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.stats() });

      notifications.show({
        title: '削除完了',
        message: '事前登録を削除しました',
        color: 'green',
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      notifications.show({
        title: '削除エラー',
        message: error.response?.data?.message || '削除に失敗しました',
        color: 'red',
      });
    },
  });
};

// 更新（管理画面用）
export const useUpdatePreRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CustomerPreRegistrationFormInput }) =>
      customerPreRegistrationApi.update(id, data),
    onSuccess: (data) => {
      // 関連クエリを無効化して再取得
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: customerPreRegistrationKeys.stats() });

      notifications.show({
        title: '更新完了',
        message: '事前登録を更新しました',
        color: 'green',
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      notifications.show({
        title: '更新エラー',
        message: error.response?.data?.message || '更新に失敗しました',
        color: 'red',
      });
    },
  });
};
